import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge, Box, Button, Card, Flex, FormControl, FormLabel, HStack, Input,
  Spinner, Switch, Tab, TabList, Tabs, Text, useToast, VStack,
} from '@chakra-ui/react';
import { MdArrowBack } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../services/apiClient';
import { RichTextEditor } from './components/RichTextEditor';
import { useContentNode, useUpdateNode, useUploadAsset, useUpsertTranslation } from './hooks/useContent';
import type { ContentNodeDetailDto } from './api/contentApi';

const LANGS = ['en', 'uk'];
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

interface Draft {
  title: string;
  bodyHtml: string;
  summary: string;
  rowVersion: string;
}

function buildDrafts(detail: ContentNodeDetailDto | undefined): Record<string, Draft> {
  const drafts: Record<string, Draft> = {};
  for (const lang of LANGS) {
    const tr = detail?.translations.find((x) => x.languageCode === lang);
    drafts[lang] = {
      title: tr?.title ?? '',
      bodyHtml: tr?.bodyHtml ?? '',
      summary: tr?.summary ?? '',
      rowVersion: tr?.rowVersion ?? EMPTY_GUID,
    };
  }
  return drafts;
}

export function ContentEditPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: detail, isLoading } = useContentNode(id);
  const upsert = useUpsertTranslation();
  const updateNode = useUpdateNode();
  const uploadAsset = useUploadAsset();

  const [drafts, setDrafts] = useState<Record<string, Draft>>(() => buildDrafts(undefined));
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (detail) setDrafts(buildDrafts(detail));
  }, [detail]);

  if (isLoading || !detail) {
    return <Flex justify="center" py="60px"><Spinner /></Flex>;
  }

  const node = detail.node;
  const lang = LANGS[tabIndex];
  const draft = drafts[lang];

  const setDraft = (patch: Partial<Draft>) =>
    setDrafts((d) => ({ ...d, [lang]: { ...d[lang], ...patch } }));

  const save = async () => {
    if (!id) return;
    try {
      await upsert.mutateAsync({
        id,
        lang,
        req: { title: draft.title, bodyHtml: draft.bodyHtml, summary: draft.summary || null, rowVersion: draft.rowVersion },
      });
      toast({ title: t('admin.content.saved'), status: 'success', duration: 3000 });
    } catch (e) {
      const conflict = e instanceof ApiError && e.status === 409;
      toast({
        title: conflict ? t('admin.content.conflict') : t('admin.content.actionFailed'),
        status: 'error',
        duration: conflict ? 6000 : 5000,
      });
    }
  };

  const toggleStatus = async () => {
    if (!id) return;
    const next = node.status === 'published' ? 'hidden' : 'published';
    try {
      await updateNode.mutateAsync({ id, req: { status: next } });
      toast({ title: t('admin.content.saved'), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('admin.content.actionFailed'), status: 'error', duration: 5000 });
    }
  };

  const onUploadImage = async (file: File) => {
    const result = await uploadAsset.mutateAsync(file);
    return result.url;
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} maxW="900px">
      <Button variant="ghost" leftIcon={<MdArrowBack />} mb="12px" pl={0} onClick={() => navigate('/content')}>
        {t('common.back')}
      </Button>
      <Card p="24px">
        <Flex align="center" mb="16px" gap="10px" wrap="wrap">
          <Text fontSize="md" fontWeight="700" color="secondaryGray.700">/{node.slug}</Text>
          <HStack ml="auto">
            <Badge colorScheme="purple">{node.type}</Badge>
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel htmlFor="pub-switch" mb="0" fontSize="sm">{t('admin.content.published')}</FormLabel>
              <Switch id="pub-switch" isChecked={node.status === 'published'} onChange={toggleStatus} isDisabled={updateNode.isPending} colorScheme="green" />
            </FormControl>
          </HStack>
        </Flex>

        <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="purple">
          <TabList>
            {LANGS.map((l) => <Tab key={l}>{l.toUpperCase()}</Tab>)}
          </TabList>
        </Tabs>

        <VStack mt="16px" align="stretch" spacing="14px">
          <FormControl>
            <FormLabel>{t('admin.content.fieldTitle')}</FormLabel>
            <Input value={draft.title} onChange={(e) => setDraft({ title: e.target.value })} />
          </FormControl>
          <FormControl>
            <FormLabel>{t('admin.content.body')}</FormLabel>
            <RichTextEditor value={draft.bodyHtml} onChange={(html) => setDraft({ bodyHtml: html })} onUploadImage={onUploadImage} />
          </FormControl>
          <FormControl>
            <FormLabel>{t('admin.content.summary')}</FormLabel>
            <Input value={draft.summary} onChange={(e) => setDraft({ summary: e.target.value })} placeholder={t('admin.content.summaryHint')} />
          </FormControl>
        </VStack>

        <Flex mt="20px">
          <Button colorScheme="purple" onClick={save} isLoading={upsert.isPending} ml="auto">
            {t('common.save')}
          </Button>
        </Flex>
      </Card>
    </Box>
  );
}
